import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

/**
 * Confirmation Dialog component
 * Displays a modal dialog for user confirmation with customizable content and actions
 * @param {Object} props Component props
 * @param {boolean} props.open Whether the dialog is open
 * @param {Function} props.onClose Function to handle dialog close
 * @param {Function} props.onConfirm Function to handle confirmation
 * @param {string} props.title Dialog title
 * @param {string} props.message Dialog message
 * @param {string} [props.confirmText] Text for confirm button
 * @param {string} [props.cancelText] Text for cancel button
 * @param {'error'|'warning'|'info'} [props.severity] Severity level of the dialog
 * @param {boolean} [props.loading] Whether the confirmation action is loading
 * @returns {JSX.Element} The ConfirmDialog component
 */
const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  severity = "warning",
  loading = false,
}) => {
  const { t } = useTranslation();

  // Get color based on severity
  const getColor = () => {
    switch (severity) {
      case "error":
        return "error.main";
      case "warning":
        return "warning.main";
      case "info":
        return "info.main";
      default:
        return "warning.main";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
    >
      <DialogTitle id="confirm-dialog-title">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" component="span" color={getColor()}>
            {title}
          </Typography>
          {!loading && (
            <IconButton aria-label="close" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        <DialogContentText id="confirm-dialog-description">
          {message}
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          {cancelText || t("Cancel")}
        </Button>
        <Button
          onClick={onConfirm}
          color={severity}
          variant="contained"
          disabled={loading}
        >
          {loading ? t("Processing...") : confirmText || t("Confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  severity: PropTypes.oneOf(["error", "warning", "info"]),
  loading: PropTypes.bool,
};

export default ConfirmDialog;
